import type { EventType } from './equipment'

// ─── Maintenance work-order task (execution layer) ───────────────────────────
//
// A dated, assignable maintenance event a technician executes. This is the
// front-end shape for the "My List" worklist; the persisted backend model
// (MaintenanceEvent) lands in subtask 86ey7d8n2 and this stays mock-backed
// until then.

/**
 * Preventive vs Corrective — the P / C glyph badge in the worklist.
 *
 * `process` is the third kind: a process-step equipment booking, derived at read
 * time from an EquipmentReservation rather than a persisted MaintenanceEvent. It
 * is neither preventive nor corrective, so it must never satisfy the PM/CM
 * filters or stat cards.
 */
export type TaskClass = 'preventive' | 'corrective' | 'process' | 'inventory'

/**
 * The classes a human may author. `process` is derived, never created here;
 * `inventory` is made by an inventory action (z8nrz7fjzc), never by hand.
 */
export type MaintenanceTaskClass = Exclude<TaskClass, 'process' | 'inventory'>

/** Catalog code for a process-step booking. Mirrors the backend STANDARD_EVENT_TYPES. */
export const PROCESS_EVENT_TYPE_CODE = 'process'

/**
 * Catalog codes that resolve a label/colour but may NOT back a maintenance
 * schedule cell or an ad-hoc event. Mirrors the backend
 * `NON_SCHEDULABLE_EVENT_TYPE_CODES` — the server rejects these with a 400, so
 * every picker must filter them out rather than offer a guaranteed failure.
 */
export const NON_SCHEDULABLE_EVENT_TYPE_CODES: readonly string[] = [PROCESS_EVENT_TYPE_CODE]

export function isSchedulableEventType(code: string): boolean {
  return !NON_SCHEDULABLE_EVENT_TYPE_CODES.includes(code)
}

/**
 * Execution status. `due` = scheduled / not-yet-started and within its window.
 *
 * `reserved` belongs only to reservation-derived rows: a hold has no maintenance
 * lifecycle, and calling it `due` would invite a technician to start work that
 * the process step — not this worklist — owns.
 */
export type TaskStatus = 'due' | 'started' | 'overdue' | 'completed' | 'reserved'

export type MaintenanceTask = {
  id: string
  taskClass: TaskClass
  /** Equipment unit id, e.g. "Apheresis machine-A2" — or, for a task on a material, its name. */
  equipmentId: string
  /** Process area the unit serves, e.g. "Production" / "Cell Culture"; a material's type. */
  usedBy: string
  /** Set when the task is on a material (Inventory's tasks, z8nrz7fjzc) instead of a unit. */
  material?: { id: string; code: string; type: string; lot?: { id: string; number: string } }
  /** Set when the task is receiving a purchase order (z8nrz7fjzc). */
  purchaseOrder?: { id: string; number: string; supplierName: string }
  /**
   * Made by an inventory action — a lot received, an order sent, a transfer
   * ready (z8nrz7fjzc) — and ended by it, so it cannot be reopened here.
   */
  fromAction?: boolean
  /** Event-type catalog code (calibration, corrective_maintenance, …). */
  eventType: EventType
  /** Priority — 1 (highest) … 5. Null when the source carries none (reservations). */
  priority: number | null
  /** ISO date the work may start (optional). */
  earlyStart?: string
  /** ISO due date. */
  dueDate: string
  /** When it was created (ISO datetime): the newest first among same-day work. */
  createdAt?: string
  planHours: number
  /** Assignee display name. */
  assigned: string
  /** Assignee User id, for the edit picker (undefined when unassigned). */
  assigneeId?: string
  /** Governing SOP / procedure reference, when linked. */
  sopRef?: string
  status: TaskStatus
  /**
   * Overdue now, as the server derives it (z8nrz7dyc7). Independent of
   * `status`: a `started` task can be overdue. Undefined reads as not overdue,
   * which is what every reservation row is.
   */
  overdue?: boolean
  /** Whole days past the due date while overdue, counted from the due date. */
  daysOverdue?: number | null
  /** Completed after its due date (plus grace). Never overdue at the same time. */
  completedLate?: boolean
  /**
   * Cadence this ONE task re-paints on the calendar at, until it is completed.
   * Undefined for an ordinary one-off task. It changes nothing about the row's
   * own status or due date — a repeat is how the task is displayed on the
   * calendar, not a series of tasks.
   */
  repeatCycle?: string
  /** Pre-start checklist authored on the event's equipment type, ordered. */
  checklist?: { id: string; label: string; required: boolean }[]
  /**
   * The procedure (forms) the task carries (z8nrz7fgzw). When set, Start goes
   * straight to its run and completing the forms ends the task.
   */
  procedureTemplate?: { id: string; name: string }
  /**
   * True for rows this worklist only displays. The execution controls
   * (Start / End / Undo End / Edit) are not rendered for them at all — the row's
   * lifecycle is owned elsewhere, so offering an action that would 404 is worse
   * than offering none.
   */
  readOnly?: boolean
  /** The booked window, when the row represents one (ISO instants, not dates). */
  window?: { startsAt: string; endsAt: string }
  /**
   * The unit the row is on is out of service now (z8nrz7fkgd): an overdue task
   * reads "Out of service · overdue", a booking warns that it stands on one.
   */
  unitOutOfService?: boolean
}

/** Status pill styling — one hex per state; rendered as a soft tinted pill + dot. */
export const TASK_STATUS_META: Record<TaskStatus, { label: string; color: string }> = {
  due: { label: 'Due', color: '#2563eb' },
  started: { label: 'Started', color: '#d97706' },
  overdue: { label: 'Overdue', color: '#dc2626' },
  completed: { label: 'Completed', color: '#16a34a' },
  reserved: { label: 'Reserved', color: '#7c3aed' },
}

/**
 * P / C class → glyph badge hue + full label.
 *
 * `process` uses R (reserved) because P is already Preventive — a second P in
 * the same column would be unreadable at a glance, which is the only thing that
 * column is for.
 */
export const TASK_CLASS_META: Record<TaskClass, { glyph: string; color: string; label: string }> = {
  preventive: { glyph: 'P', color: '#2563eb', label: 'Preventive' },
  corrective: { glyph: 'C', color: '#dc2626', label: 'Corrective' },
  process: { glyph: 'R', color: '#7c3aed', label: 'Process reservation' },
  // Made by an inventory action (z8nrz7fjzc): a lot received, an order sent.
  inventory: { glyph: 'I', color: '#0d9488', label: 'Inventory' },
}

/**
 * Standard event-type palette for the mock phase — mirrors the seeded tenant
 * catalog. Once My List is wired to the API (subtask 86ey7d8n2) these resolve
 * from `useEventTypes`; the color is the hex of the catalog's `-500` token.
 */
export const STANDARD_EVENT_META: Record<string, { label: string; color: string }> = {
  preventive_maintenance: { label: 'Preventive Maintenance (PM)', color: '#3b82f6' },
  corrective_maintenance: { label: 'Corrective Maintenance', color: '#f59e0b' },
  // One-off work such as a spill (z8nrz7f9u6).
  corrective_action: { label: 'Corrective Action', color: '#ea580c' },
  cleaning: { label: 'Cleaning/Sanitization', color: '#22c55e' },
  calibration: { label: 'Calibration', color: '#8b5cf6' },
  inspection: { label: 'Inspection', color: '#14b8a6' },
  verification: { label: 'Verification', color: '#f43f5e' },
  performance_qualification: { label: 'Performance Qualification (PQ)', color: '#d946ef' },
  // Not maintenance — a process-step equipment booking. Mirrors the backend's
  // STANDARD_EVENT_TYPES entry; the violet is the colour the planner calendar
  // already used for its reservation layer.
  process: { label: 'Process', color: '#7c3aed' },
  // Inventory's tasks, made by inventory actions (z8nrz7fjzc). Mirrors the
  // backend's TRIGGER_EVENT_LABEL; no tenant catalog lists them.
  lot_release: { label: 'Lot release', color: '#0d9488' },
  order_receipt: { label: 'Receive order', color: '#0891b2' },
  storage_transfer: { label: 'Storage transfer', color: '#6366f1' },
}

/** An event type's name and colour, keyed by code. */
export type EventTypeCatalog = ReadonlyMap<string, { label: string; color: string }>

/** Event-type code as words ("quality_check" → "Quality check"), never the raw code. */
export function humanizeEventType(code: string): string {
  const words = code.replace(/_/g, ' ').trim()
  return words ? words[0].toUpperCase() + words.slice(1) : code
}

/**
 * An event type's name and colour: from the tenant's own catalog when given
 * (`useEventTypeCatalog`), else the standard list, else the code as words —
 * so no screen ever shows a raw code such as `cleaning_sanitization`
 * (z8nrz7fb9k). Pass the catalog wherever one is at hand: a tenant's custom
 * types and renamed standard ones are only in there.
 */
export function eventMeta(code: EventType, catalog?: EventTypeCatalog): { label: string; color: string } {
  return catalog?.get(code) ?? STANDARD_EVENT_META[code] ?? { label: humanizeEventType(code), color: '#64748b' }
}

/** Quick-filter chips shown above the worklist. */
export type TaskFilter =
  | 'all'
  | 'due'
  | 'started'
  | 'overdue'
  | 'pm'
  | 'cm'
  | 'process'
  | 'assigned'
  | 'unassigned'

/** Sentinel display name for an event with no assignee (see apiEventToTask). */
export const UNASSIGNED_LABEL = 'Unassigned'
